import React, { useState } from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
} from '@material-ui/core'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useAllCountries } from '../../../graphql/hooks/localization'
import { ICity } from '../../../graphql/types/localization'
import { CorporateTypesMultiSelect } from '../corporateTypes/CorporateTypesMultiSelect'
import { Loader } from '../../../components/UI'
import { LoadableButton } from '../../../components/Form'

interface IProps {
  open: boolean
  onSubmit: (filter: ICorporateFilterFormValues) => void
  onCancel: () => void
}

export interface ICorporateFilterFormValues {
  corporationName: string
  countryId: number | null
  citiesIds: number[]
  corporationTypesIds: number[]
}

const corporateFilterSchema = Yup.object().shape({
  corporationName: Yup.string(),
  countryId: Yup.number()
    .integer()
    .nullable(),
  citiesIds: Yup.array().of(Yup.number()),
  corporationTypesIds: Yup.array().of(Yup.number()),
})

export const CorporatesFilterModal: React.FC<IProps> = ({ open, onSubmit, onCancel }) => {
  const { countries, loading: loadingCountries } = useAllCountries()
  const [cities, setCities] = useState<ICity[]>([])

  const formik = useFormik<ICorporateFilterFormValues>({
    initialValues: {
      corporationName: '',
      countryId: null,
      citiesIds: [],
      corporationTypesIds: [],
    },
    validationSchema: corporateFilterSchema,
    onSubmit(values, formikHelpers) {
      onSubmit(values)
    },
  })

  const handleReset = () => {
    onSubmit({
      corporationName: '',
      countryId: null,
      citiesIds: [],
      corporationTypesIds: [],
    })
  }

  const handleChangeCountryId = (id: string, resetCities: boolean = true) => {
    if (resetCities) {
      formik.setFieldValue('citiesIds', [])
    }

    if (id === '') {
      formik.setFieldValue('countryId', null)
      setCities([])
      return
    }

    const selectedCountry = countries.find(c => c.id === parseInt(id))
    if (selectedCountry) {
      formik.setFieldValue('countryId', selectedCountry.id)
      setCities([...selectedCountry.cities])
    }
  }

  const handleSelectCity = (city: ICity) => {
    if (formik.values.countryId !== city.countryId) {
      return
    }

    if (values.citiesIds.includes(city.id)) {
      formik.setFieldValue(
        'citiesIds',
        formik.values.citiesIds.filter(c => c !== city.id)
      )
    } else {
      formik.setFieldValue('citiesIds', [...formik.values.citiesIds, city.id])
    }
  }

  const { values, errors, handleChange, setFieldValue, submitForm } = formik

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <DialogTitle id='form-dialog-title'>Фильтр корпорации</DialogTitle>
      <DialogContent>
        <Box>
          <Box mb={2}>
            <TextField
              label='Название корпорации'
              name='corporationName'
              value={values.corporationName}
              onChange={handleChange}
              fullWidth
              margin='normal'
              error={!!errors.corporationName}
              helperText={errors.corporationName}
            />
          </Box>

          <Box mb={2}>
            <CorporateTypesMultiSelect
              values={values.corporationTypesIds}
              onChange={types => setFieldValue('corporationTypesIds', types)}
            />
          </Box>

          <Box>
            {loadingCountries ? (
              <Loader />
            ) : (
              <TextField
                placeholder='Страна'
                label='Страна'
                value={values.countryId ?? ''}
                onChange={e => handleChangeCountryId(e.target.value)}
                error={!!errors.countryId}
                margin='normal'
                fullWidth
                select
              >
                <MenuItem value={''}>{'Без страны'}</MenuItem>
                {countries.map(country => (
                  <MenuItem key={`country-${country.id}`} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {!loadingCountries && cities.length > 0 && (
            <Box mb={4}>
              {cities.map(city => (
                <FormControlLabel
                  key={`city-${city.id}`}
                  control={
                    <Checkbox
                      checked={values.citiesIds.includes(city.id) ?? false}
                      onChange={() => handleSelectCity(city)}
                      value={city.id}
                    />
                  }
                  label={city.name}
                />
              ))}
            </Box>
          )}

          <Box pt={4} pb={1} display='flex' flexDirection='row' justifyContent='space-between'>
            <Button onClick={onCancel}>Отмена</Button>
            <Box display='flex' flexDirection='row'>
              <Button onClick={handleReset} color='default'>
                Сбросить
              </Button>
              <Box width={8} />
              <LoadableButton color='primary' variant='contained' onClick={submitForm}>
                Применить
              </LoadableButton>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
